import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import useStore from '../store';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Clock, Euro, Star, Users, ChefHat, Utensils, BookOpen, Heart, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to convert tag to translation key (e.g., "gluten-free" -> "gluten_free")
const getTagKey = (tag) => tag.replace(/-/g, '_');

function RecipeDetailPage() {
  const { t, i18n } = useTranslation();
  const { user } = useStore();
  const { recipeId } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingDialog, setRatingDialog] = useState(false);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [favLoading, setFavLoading] = useState(false);

  useEffect(() => {
    fetchRecipe();
    fetchReviews();
    checkFavorite();
  }, [recipeId, i18n.language]);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const resp = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setCurrentUser(resp.data);
      } catch (e) {}
    };
    fetchCurrentUser();
  }, []);

  const checkFavorite = async () => {
    try {
      const response = await axios.get(`${API}/favorites/check/${recipeId}`, { withCredentials: true });
      setIsFavorite(response.data.is_favorite);
    } catch (error) {
      console.error('Failed to check favorite:', error);
    }
  };

  const toggleFavorite = async () => {
    setFavLoading(true);
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${recipeId}`, { withCredentials: true });
        setIsFavorite(false);
        toast.success(t('favorites.removed'));
      } else {
        await axios.post(`${API}/favorites/${recipeId}`, {}, { withCredentials: true });
        setIsFavorite(true);
        toast.success(t('recipe.added_to_favorites'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setFavLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/recipes/${recipeId}`, { withCredentials: true });
      toast.success('Recept succesvol verwijderd');
      navigate('/recipes');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verwijderen mislukt');
    } finally {
      setDeleting(false);
      setDeleteDialog(false);
    }
  };

  const fetchRecipe = async () => {
    try {
      const response = await axios.get(`${API}/recipes/${recipeId}?lang=${i18n.language}`, { withCredentials: true });
      setRecipe(response.data.recipe);
    } catch (error) {
      console.error('Failed to fetch recipe:', error);
      toast.error(t('common.error'));
      navigate('/recipes');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/recipes/${recipeId}/reviews`, { withCredentials: true });
      setReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  };

  const handleSubmitRating = async () => {
    if (rating === 0) {
      toast.error(t('rating.select_stars'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/recipes/${recipeId}/review`, {
        rating: rating,
        comment: reviewText
      }, { withCredentials: true });
      toast.success(t('rating.thanks'));
      setRatingDialog(false);
      setRating(0);
      setReviewText('');
      fetchRecipe();
      fetchReviews();
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!recipe) return null;

  return (
    <div className="py-4 sm:py-8">
      <div className="container mx-auto px-3 sm:px-4 max-w-4xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/recipes')}
          className="mb-4 sm:mb-6"
          data-testid="back-to-recipes-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>

        {/* Recipe Header */}
        <div className="mb-4 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-[#2C3E2D] mb-3 sm:mb-4" data-testid="recipe-title">
            {recipe.title}
          </h1>
          <p className="text-base sm:text-lg text-gray-600 mb-4">{recipe.description}</p>
          
          <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              <span className="font-medium">{recipe.cook_time_min} {t('common.min')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Euro className="w-5 h-5 text-primary" />
              <span className="font-medium">&euro;{recipe.price_estimate_eur?.toFixed(2)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              <span className="font-medium">{recipe.servings || 4} {t('recipe.servings')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              <span className="font-medium">{recipe.ratings_avg?.toFixed(1) || '0.0'}</span>
              <span className="text-gray-500">({recipe.ratings_count || 0} {t('recipe.reviews')})</span>
            </div>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mt-4">
            {(Array.isArray(recipe.cuisines) ? recipe.cuisines : [recipe.cuisines]).filter(Boolean).map((cuisine, idx) => (
              <span key={idx} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                {t(`cuisines.${cuisine.toLowerCase().replace(/ /g, '_')}`) || cuisine}
              </span>
            ))}
            {recipe.dietary_tags?.map((tag, idx) => (
              <span key={idx} className="px-3 py-1 bg-success/10 text-success rounded-full text-sm font-medium">
                {t(`recipes.${getTagKey(tag)}`) || tag}
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {/* Ingredients */}
          <Card className="md:col-span-1" data-testid="ingredients-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Utensils className="w-5 h-5 text-primary" />
                {t('recipe.ingredients')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {recipe.ingredients?.map((ing, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <span className="w-2 h-2 bg-primary rounded-full mt-1.5 flex-shrink-0" />
                    <span>
                      <span className="font-medium">{ing.amount} {ing.unit}</span> {ing.name}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card className="md:col-span-2" data-testid="instructions-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary" />
                {t('recipe.instructions')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-4">
                {recipe.steps?.map((step, idx) => (
                  <li key={idx} className="flex gap-4">
                    <span className="flex-shrink-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold text-sm">
                      {idx + 1}
                    </span>
                    <p className="text-gray-700 pt-1">{step}</p>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>

        {/* Action Button */}
        <div className="mt-6 flex gap-4 flex-wrap">
          <Button
            onClick={() => setRatingDialog(true)}
            className="bg-secondary hover:bg-secondary-hover text-white"
            data-testid="rate-recipe-btn"
          >
            <Star className="w-4 h-4 mr-2" />
            {t('rating.rate_recipe')}
          </Button>
          <Button
            variant={isFavorite ? "default" : "outline"}
            onClick={toggleFavorite}
            disabled={favLoading}
            className={isFavorite ? "bg-red-500 hover:bg-red-600 text-white" : ""}
            data-testid="favorite-btn"
          >
            <Heart className={`w-4 h-4 mr-2 ${isFavorite ? 'fill-white' : ''}`} />
            {isFavorite ? t('favorites.remove_favorite') : t('recipe.add_to_favorites')}
          </Button>
            {user && (user.user_id === recipe.created_by || user.role === 'superadmin' || user.role === 'admin') && (
              <Button
                variant="destructive"
                onClick={() => setDeleteDialog(true)}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="delete-recipe-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('recipe.delete_recipe')}
              </Button>
            )}
        </div>

        {/* Reviews Section */}
        {reviews.length > 0 && (
          <Card className="mt-8" data-testid="reviews-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                {t('recipe.reviews')} ({reviews.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {reviews.map((review, idx) => (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{review.user_name}</span>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${star <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                    </div>
                    {review.comment && <p className="text-sm text-gray-600">{review.comment}</p>}
                    <p className="text-xs text-gray-400 mt-2">{new Date(review.created_at).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delete Confirm Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Recept verwijderen</DialogTitle>
          </DialogHeader>
          <p className="py-4 text-gray-600">Weet je zeker dat je <strong>{recipe.title}</strong> wilt verwijderen? Dit kan niet ongedaan worden gemaakt.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>Annuleren</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              <Trash2 className="w-4 h-4 mr-2" />
              {deleting ? 'Bezig...' : 'Ja, verwijder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rating Dialog */}
        <Dialog open={ratingDialog} onOpenChange={setRatingDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('rating.rate_recipe')}: {recipe.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">{t('rating.how_many_stars')}</p>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      className="focus:outline-none"
                      data-testid={`star-${star}`}
                    >
                      <Star
                        className={`w-8 h-8 transition-colors ${
                          star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-2">{t('rating.write_review')}</p>
                <Textarea
                  placeholder={t('rating.review_placeholder')}
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  rows={4}
                  data-testid="review-textarea"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRatingDialog(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSubmitRating} disabled={submitting} data-testid="submit-rating-btn">
                {submitting ? t('common.loading') : t('rating.submit')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default RecipeDetailPage;
